import React from 'react';
import {
  AppAlert,
  AppBreadcrumb,
  AppButton,
  AppCard,
  AppCheckbox,
  AppContainer,
} from '@/components/atoms';
import { Edit } from '@/components/atoms/AppIcon';
import AppTable from '@/components/atoms/AppTable';
import SiteAdminNotFoundTemplate from '@/components/templates/SiteAdminNotFoundTemplate';
import {
  parseClinicLocationId,
  useClinicLocationQuery,
} from '@/hooks/admin/useClinicLocationCreate';
import { SERVICE_TYPE_FALLBACK } from '@/constants/supportRequest';
import type {
  ClinicLocationManagerItem,
  ClinicLocationMassagerItem,
} from '@/services/admin/clinic-locations';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { convGtJDate } from '@/utils/dateConverter';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import type { TableProps } from 'antd';
import { Spin } from 'antd';
import { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import type { AxiosError } from 'axios';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

const HEALTH_CENTER_LABELS: Record<number, string> = {
  1: '施術所所在地',
  2: '出張専門施術者住所',
};

const AVAILABLE_TO_INDEFINITE = '2100-12-31';

function isExpired(availableTo: string | null | undefined): boolean {
  if (!availableTo || availableTo === AVAILABLE_TO_INDEFINITE) return false;
  const today = new Date().toISOString().slice(0, 10);
  return availableTo < today;
}

/**
 * Format period for 拠点情報 box.
 * Hides the TO date when available_to is null or 2100-12-31, but always shows "から".
 */
function formatPeriod(from: string | null, to: string | null): string {
  const fromStr = convGtJDate(from ?? '');
  if (!fromStr) return '';
  const isIndefinite = !to || to === AVAILABLE_TO_INDEFINITE;
  if (isIndefinite) return `${fromStr} -`;
  return `${fromStr} - ${convGtJDate(to)}`;
}

/**
 * Format period for therapist/manager tables.
 * Renders 2 lines: line 1 = "{from}から", line 2 = "{to}" (empty when indefinite).
 */
function formatTherapistPeriod(from: string | null, to: string | null): React.ReactNode {
  const fromStr = convGtJDate(from ?? '');
  if (!fromStr) return '';
  const isIndefinite = !to || to === AVAILABLE_TO_INDEFINITE;
  const toStr = isIndefinite ? '' : convGtJDate(to);
  return (
    <>
      {fromStr}から
      <br />
      {toStr}
    </>
  );
}

function formatPostalCode(code: string | null | undefined): string {
  if (code == null || code === '') return '';
  const digits = code.replace(/\D/g, '');
  if (digits.length !== 7) return code;
  return `${digits.slice(0, 3)} - ${digits.slice(3, 7)}`;
}

export default function ClinicLocationDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { site, id } = useParams<{ site: string; id: string }>();
  const { currentSite } = useSiteAdminAuth();

  const siteType = site ?? currentSite ?? 'medix';
  const locationId = id ?? null;
  const locationIdNum = parseClinicLocationId(locationId);

  const [showOutsidePeriod, setShowOutsidePeriod] = React.useState(false);
  const [errorBanner, setErrorBanner] = React.useState<string | null>(null);

  React.useEffect(() => {
    const state = location.state as { errorBanner?: string } | null;
    if (state?.errorBanner) {
      setErrorBanner(state.errorBanner);
      window.scrollTo({ top: 0 });
      // Clear router state so banner doesn't re-show on back/refresh
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const locationQuery = useClinicLocationQuery(siteType, locationId, {
    refetchOnMount: 'always',
    showOutsidePeriod,
  });
  const data = locationQuery.data?.data;
  const massagers = data?.massagers ?? [];
  const managers = data?.managers ?? [];

  const breadcrumbItems: BreadcrumbItemType[] = [
    { title: '拠点', href: `/${siteType}/admin/clinic_locations` },
    { title: ':id' },
  ];

  const handleEdit = () => {
    if (locationIdNum != null) {
      navigate(`/${siteType}/admin/clinic_locations/${locationIdNum}/edit`);
    }
  };

  const handleBack = () => {
    if (data?.clinic_id != null) {
      navigate(`/${siteType}/admin/clinics/${data.clinic_id}`);
    } else {
      navigate(`/${siteType}/admin/clinic_locations`);
    }
  };

  // Only block full-page rendering during the initial load.
  if (locationQuery.isLoading && !data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spin size="large" />
      </div>
    );
  }

  if (locationQuery.isError && !data) {
    const status = (locationQuery.error as AxiosError)?.response?.status;
    if (status === 404 || !data) return <SiteAdminNotFoundTemplate />;
    return <SiteAdminNotFoundTemplate />;
  }

  if (!data) {
    return <SiteAdminNotFoundTemplate />;
  }

  const infoRows: { label: string; value: string }[] = [
    { label: '治療院ID', value: data.clinic_number ?? '' },
    {
      label: 'サービス区分',
      value:
        SERVICE_TYPE_FALLBACK[(data.service_type ?? '').toString().toUpperCase()] ?? '',
    },
    { label: '拠点名', value: data.location_name ?? '' },
    { label: '拠点名カナ', value: data.location_name_kana ?? '' },
    { label: '有効期限', value: formatPeriod(data.available_from, data.available_to ?? null) },
    {
      label: '保健所登録区分',
      value: HEALTH_CENTER_LABELS[data.health_center_regist_type_id] ?? '',
    },
    {
      label: '住所',
      value: [formatPostalCode(data.postal_code), data.address1, data.address2, data.address3]
        .filter(Boolean)
        .join(' '),
    },
    { label: '電話番号', value: data.telephone ?? '' },
    { label: 'レセプト表示住所', value: data.rezept_address ?? '' },
    { label: 'FAX', value: data.fax ?? '' },
    {
      label: '法人個人',
      value:
        data.is_compnay === 1 ? '法人' : data.is_compnay === 0 ? '個人' : '',
    },
    { label: '住所拠点名', value: data.base_name ?? '' },
    { label: '開設者名', value: data.founder_name ?? '' },
    { label: '開設者名カナ', value: data.founder_name_kana ?? '' },
    {
      label: '開設者住所',
      value:
        [
          formatPostalCode(data.founder_postal_code),
          data.founder_address1,
          data.founder_address2,
          data.founder_address3,
        ]
          .filter(Boolean)
          .join(' ') ?? '',
    },
    { label: '開設者電話番号', value: data.founder_telephone ?? '' },
    {
      label: '開設者生年月日',
      value: data.founder_date_of_birth ? convGtJDate(data.founder_date_of_birth) : '',
    },
  ];

  const massagerColumns: TableProps<ClinicLocationMassagerItem>['columns'] = [
    {
      title: '施術者',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (_, record) =>
        renderTableCellTruncate(record.name || null, {
          lineClamp: 1,
          width: 200,
        }),
    },
    {
      title: '有効期限（はり・きゅう）',
      key: 'period_a2',
      width: '15%',
      render: (_, r) => {
        if (r.available_from == null || String(r.available_from).trim() === '') return '';
        const text = formatTherapistPeriod(r.available_from, r.available_to ?? null);
        return isExpired(r.available_to) ? <span className="text-gray-400">{text}</span> : text;
      },
    },
    {
      title: '有効期限（マッサージ）',
      key: 'period_r2',
      width: '15%',
      render: (_, r) => {
        if (r.available_from_r2 == null || String(r.available_from_r2).trim() === '') return '';
        const text = formatTherapistPeriod(r.available_from_r2, r.available_to_r2 ?? null);
        return isExpired(r.available_to_r2) ? <span className="text-gray-400">{text}</span> : text;
      },
    },
    {
      title: '資格',
      key: 'certificate',
      dataIndex: 'certificate',
      width: '25%',
      render: (cert: string | null | undefined) => cert ?? '—',
    },
    {
      title: '操作',
      key: 'action',
      width: '15%',
      render: (_, record) =>
        locationIdNum != null ? (
          <Link
            to={`/${siteType}/admin/clinic_locations/${locationIdNum}/clinic_location_massagers/${record.id}/edit`}
          >
            <AppButton type="primary" size="small">
              施術者編集
            </AppButton>
          </Link>
        ) : null,
    },
  ];

  const managerColumns: TableProps<ClinicLocationManagerItem>['columns'] = [
    {
      title: '施術管理者',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (_, record) =>
        renderTableCellTruncate(record.name || null, {
          lineClamp: 1,
          width: 200,
        }),
    },
    {
      title: '有効期限（はり・きゅう）',
      key: 'period_a2',
      render: (_, r) =>
        formatTherapistPeriod(r.available_from_a2 ?? null, r.available_to_a2 ?? null),
      width: '15%',
    },
    {
      title: '有効期限（マッサージ）',
      key: 'period_r2',
      render: (_, r) =>
        formatTherapistPeriod(r.available_from_r2 ?? null, r.available_to_r2 ?? null),
      width: '15%',
    },
    {
      title: 'サビース管理',
      dataIndex: 'service_type',
      key: 'service_type',
      width: '20%',
      render: (serviceType: string | null | undefined) => {
        const normalized = (serviceType ?? '').toString().toUpperCase();
        if (!normalized) return '';
        return SERVICE_TYPE_FALLBACK[normalized] ?? serviceType;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: '15%',
      render: (_, record) => (
        <Link
          to={`/${siteType}/admin/clinic_locations/${locationIdNum}/clinic_location_managers/${record.id}/edit`}
        >
          <AppButton type="primary" size="small">
            施術管理者編集
          </AppButton>
        </Link>
      ),
    },
  ];

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />

      {errorBanner && <AppAlert type="error" message={errorBanner} showIcon />}

      <h1 className="text-xl font-semibold">
        拠点情報詳細:{data.id}
      </h1>

      <AppCard title="拠点情報" className="w-full">
        {managers.length === 0 && (
          <p className="mb-4 text-base font-medium text-red-600">
            施術管理者の登録がありません。登録してください
          </p>
        )}
        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          {infoRows.map((row, i) => (
            <div key={i} className="flex min-w-0 flex-col sm:flex-row sm:items-start sm:gap-2">
              <dt className="min-w-[140px] shrink-0 font-bold">{row.label}</dt>
              <dd className="min-w-0 flex-1 overflow-hidden">
                {renderTableCellTruncate(
                  (row.value || '').replace(/\r?\n/g, ' ').trim() || null,
                  {
                    lineClamp: 1,
                    width: '100%',
                    emptyPlaceholder: '-',
                  }
                )}
              </dd>
            </div>
          ))}
        </dl>
      </AppCard>

      <AppCard title="施術者" className="w-full">
        <div className="mb-3 flex justify-end">
          <AppCheckbox
            checked={showOutsidePeriod}
            onChange={e => setShowOutsidePeriod(e.target.checked)}
          >
            有効期限外を表示する
          </AppCheckbox>
        </div>
        <AppTable
          columns={massagerColumns}
          dataSource={massagers}
          rowKey="id"
          pagination={false}
          loading={locationQuery.isFetching}
        />
      </AppCard>

      <AppCard title="施術管理者" className="w-full">
        <div className="mb-3">
          <Link
            to={`/${siteType}/admin/clinic_locations/${locationIdNum}/clinic_location_managers/create`}
          >
            <AppButton type="primary" size="small">
              施術管理者追加
            </AppButton>
          </Link>
        </div>
        <AppTable
          columns={managerColumns}
          dataSource={managers}
          rowKey={r => String(r.massager_id ?? r.id)}
          pagination={false}
        />
      </AppCard>

      <div className="flex flex-wrap gap-3 pt-2">
        <AppButton type="primary" icon={<Edit />} onClick={handleEdit} className="min-w-[120px]">
          編集
        </AppButton>
        <AppButton type="default" onClick={handleBack} className="min-w-[160px]">
          詳細画面へ戻る
        </AppButton>
      </div>
    </AppContainer>
  );
}
